/**
 * Utility functions for Job Material mapping, allocation flattening,
 * and entity formatting in CreateJob.
 */

/**
 * Calculates the scheduled_date (YYYY-MM-DD) for a truck allocation by taking
 * the job start_date and adding in days (scheduled_day - 1).
 */
export function calculateScheduledDate(startDate, scheduledDay = 1) {
  if (!startDate) return null;
  try {
    let dayNum = 1;
    if (Array.isArray(scheduledDay)) {
      dayNum = scheduledDay[0] || 1;
    } else if (scheduledDay != null) {
      dayNum = parseInt(scheduledDay, 10) || 1;
    }
    const offsetDays = Math.max(0, dayNum - 1);

    const cleanStr = String(startDate).split('T')[0].split(' ')[0].trim();
    const parts = cleanStr.split('-').map(Number);
    if (parts.length !== 3 || isNaN(parts[0]) || isNaN(parts[1]) || isNaN(parts[2])) {
      return cleanStr;
    }

    const d = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
    d.setUTCDate(d.getUTCDate() + offsetDays);

    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  } catch (err) {
    console.warn("[calculateScheduledDate] Failed to calculate date:", err);
    return String(startDate).split('T')[0];
  }
}

/**
 * Maps allocation board days into assigned trucks list for a material,
 * preserving live telemetry (current_coords, previous_coords, direction, destination, loads, address).
 */
export function mapAllocationsToAssignedTrucks(allocEntry, jobMatOrId, startDate = null) {
  if (!allocEntry || !allocEntry.days) return [];
  const assignedTrucks = [];
  const baseMatId = jobMatOrId?.__job_material_number__ || jobMatOrId?.job_material_number || jobMatOrId?.id || jobMatOrId?.job_number || (typeof jobMatOrId === 'string' ? jobMatOrId : 'mat');
  const baseStartDate = startDate || jobMatOrId?.start_date || null;

  const extractTruckId = (t) => {
    if (!t) return '';
    if (typeof t.truck === 'string' && t.truck.trim()) return t.truck.trim();
    if (t.truck && typeof t.truck === 'object' && (t.truck.id || t.truck.rego)) {
      return String(t.truck.id || t.truck.rego).trim();
    }
    if (t.rego) return String(t.rego).trim();
    if (t.id && typeof t.id === 'string' && t.id.includes('-')) {
      return t.id.split('-').pop();
    }
    return '';
  };

  const hasCoords = (t) => {
    const c = t && (t.current_coords || t.coordinates);
    return Boolean(c && c.lat != null && c.lng != null);
  };

  const existingAllocs = (jobMatOrId && typeof jobMatOrId === 'object' && Array.isArray(jobMatOrId.allocated_trucks))
    ? jobMatOrId.allocated_trucks
    : [];

  const existingByDayAndTruck = new Map();
  const existingCoordsByTruck = new Map();
  existingAllocs.forEach(ea => {
    const tid = extractTruckId(ea);
    if (!tid) return;
    const dNum = parseInt(ea.scheduled_day ?? 1, 10) || 1;
    existingByDayAndTruck.set(`${dNum}_${tid}`, ea);
    if (hasCoords(ea)) {
      existingCoordsByTruck.set(tid, ea);
    }
  });

  Object.entries(allocEntry.days).forEach(([dayIdx, trucksList]) => {
    if (Array.isArray(trucksList)) {
      const scheduledDay = parseInt(dayIdx, 10) + 1;
      const calcDate = baseStartDate ? calculateScheduledDate(baseStartDate, scheduledDay) : null;
      trucksList.forEach((tItem, tOrder) => {
        const truckId = tItem.truck?.id || (typeof tItem.truck === 'string' ? tItem.truck : `TRK-${tOrder}`);
        const exactExisting = existingByDayAndTruck.get(`${scheduledDay}_${truckId}`);
        const coordsSource = hasCoords(tItem)
          ? tItem
          : (hasCoords(exactExisting) ? exactExisting : existingCoordsByTruck.get(truckId));

        const resolvedCoords = coordsSource ? (coordsSource.current_coords || coordsSource.coordinates) : null;
        const allocId = exactExisting?.id || (scheduledDay > 1 ? `${baseMatId}-D${scheduledDay}-${truckId}` : `${baseMatId}-${truckId}`);

        assignedTrucks.push({
          ...(exactExisting || {}),
          truck: tItem.truck,
          trailer: tItem.trailer || tItem.truck?.default_trailer || tItem.truck?.trailer || exactExisting?.trailer || null,
          driver: tItem.driver || tItem.truck?.default_driver || exactExisting?.driver || null,
          scheduled_start_time: tItem.scheduledStartTime || tItem.scheduled_start_time || exactExisting?.scheduled_start_time || "07:00",
          scheduled_day: scheduledDay,
          scheduled_date: calcDate || tItem.scheduled_date || tItem.scheduledDate || exactExisting?.scheduled_date || null,
          dispatch_order: tOrder + 1,
          id: allocId,
          current_coords: resolvedCoords || null,
          coordinates: resolvedCoords || null,
          previous_coords: tItem.previous_coords || exactExisting?.previous_coords || coordsSource?.previous_coords || null,
          direction: tItem.direction || exactExisting?.direction || coordsSource?.direction || null,
          destination: tItem.destination || exactExisting?.destination || coordsSource?.destination || null,
          address: tItem.address || exactExisting?.address || coordsSource?.address || null,
          loads: Array.isArray(tItem.loads) ? tItem.loads : (Array.isArray(exactExisting?.loads) ? exactExisting.loads : [])
        });
      });
    }
  });

  return assignedTrucks;
}

/**
 * Builds the canonical job materials array reflecting quote materials,
 * active allocation board assignments, and nested operating_hours/fleet/schedule.
 */
export function buildJobMaterialsPayload(sourceMaterials, currentAllocations, selectedQuote, extraParams = {}) {
  if (!Array.isArray(sourceMaterials)) return [];
  const { loadingUhf = "", disposalUhf = "", siteNotes = "", startDate = null } = extraParams;

  return sourceMaterials.map(jobMat => {
    let allocEntry = Object.entries(currentAllocations || {}).find(([k, a]) => {
      if (!a) return false;
      const aName = (a.materialName || k || "").trim().toLowerCase();
      const jName = (jobMat.name || "").trim().toLowerCase();
      return aName === jName || k === String(jobMat.id) || k === String(jobMat.__job_material_number__);
    })?.[1];

    if (!allocEntry && sourceMaterials.length === 1 && currentAllocations && Object.keys(currentAllocations).length === 1) {
      const candidate = Object.values(currentAllocations)[0];
      const candName = (candidate?.materialName || "").trim().toLowerCase();
      const jName = (jobMat.name || "").trim().toLowerCase();
      if (candName && candName === jName) {
        allocEntry = candidate;
      }
    }

    const baseStartDate = startDate || jobMat.start_date || selectedQuote?.quote_date || new Date().toISOString().split('T')[0];
    const assignedTrucks = mapAllocationsToAssignedTrucks(allocEntry, jobMat, baseStartDate);

    const qMat = (selectedQuote?.quote_materials || []).find(qm => 
      qm.material_name === jobMat.name || 
      qm.name === jobMat.name ||
      (qm.id && jobMat.id && String(qm.id) === String(jobMat.id)) ||
      (qm.id && jobMat.__job_material_number__ && String(qm.id) === String(jobMat.__job_material_number__)) ||
      (qm.__job_material_number__ && jobMat.__job_material_number__ && String(qm.__job_material_number__) === String(jobMat.__job_material_number__))
    );

    const mappedMat = {
      ...jobMat,
      amount: qMat?.total_tonnes || qMat?.amount || jobMat.amount,
      load_address: (qMat && qMat.load_address !== undefined) ? qMat.load_address : (jobMat.load_address || null),
      siteAddress: (qMat && qMat.siteAddress !== undefined) ? qMat.siteAddress : (jobMat.siteAddress || ""),
      disposal_address: (qMat && qMat.disposal_address !== undefined) ? qMat.disposal_address : (jobMat.disposal_address || null),
      dispose_address: (qMat && qMat.dispose_address !== undefined) ? qMat.dispose_address : (jobMat.dispose_address || ""),
      yard: (qMat && qMat.yard !== undefined) ? qMat.yard : (jobMat.yard || null),
      yard_id: (qMat && qMat.yard_id !== undefined) ? qMat.yard_id : (jobMat.yard_id ?? null),
      yard_name: (qMat && qMat.yard_name !== undefined) ? qMat.yard_name : (jobMat.yard_name ?? null),
      suburb: (qMat && qMat.suburb !== undefined) ? qMat.suburb : (jobMat.suburb ?? ""),
      image: (qMat && qMat.image !== undefined) ? qMat.image : (jobMat.image ?? null),
      contact_name: (qMat && qMat.contact_name !== undefined) ? qMat.contact_name : (jobMat.contact_name ?? ""),
      contact_phone: (qMat && qMat.contact_phone !== undefined) ? qMat.contact_phone : (jobMat.contact_phone ?? ""),
      contact_email: (qMat && qMat.contact_email !== undefined) ? qMat.contact_email : (jobMat.contact_email ?? ""),
      contact_position: (qMat && qMat.contact_position !== undefined) ? qMat.contact_position : (jobMat.contact_position ?? ""),
      main_contact: (qMat && qMat.main_contact !== undefined) ? qMat.main_contact : (jobMat.main_contact || null),
      operating_hours: qMat?.operating_hours ? {
        start_time: qMat.operating_hours.start_time || "07:00",
        finish_time: qMat.operating_hours.finish_time || "17:00",
        load_mins: Number(qMat.operating_hours.load_mins ?? 15),
        tip_mins: Number(qMat.operating_hours.tip_mins ?? 15)
      } : (jobMat.operating_hours || {
        start_time: "07:00",
        finish_time: "17:00",
        load_mins: 15,
        tip_mins: 15
      }),
      fleet: qMat?.fleet ? {
        truck_type: qMat.fleet.truck_type || "10 wheeler - 19t",
        truck_capacity_tonne: Number(qMat.fleet.truck_capacity_tonne ?? 19),
        trailer_type: qMat.fleet.trailer_type || "8 wheeler - 20t",
        trailer_capacity_tonne: Number(qMat.fleet.trailer_capacity_tonne ?? 19),
        total_capacity_tonne: Number(qMat.fleet.total_capacity_tonne ?? 38),
        truck_day_rate: Number(qMat.fleet.truck_day_rate ?? 1400)
      } : (jobMat.fleet || {
        truck_type: "10 wheeler - 19t",
        truck_capacity_tonne: 19,
        trailer_type: "8 wheeler - 20t",
        trailer_capacity_tonne: 19,
        total_capacity_tonne: 38,
        truck_day_rate: 1400
      }),
      schedule: {
        runs_per_day: Number(qMat?.schedule?.runs_per_day ?? jobMat.schedule?.runs_per_day ?? 5),
        total_loads: Number(qMat?.schedule?.total_loads ?? jobMat.schedule?.total_loads ?? 1),
        allocated_trucks: parseInt(qMat?.schedule?.allocated_trucks ?? jobMat.schedule?.allocated_trucks, 10) || (assignedTrucks.length || 1),
        estimated_days: Number(qMat?.schedule?.estimated_days ?? jobMat.schedule?.estimated_days ?? 1.0),
        days_single_truck: Number(qMat?.schedule?.days_single_truck ?? jobMat.schedule?.days_single_truck ?? 1.0),
        full_days: Number(qMat?.schedule?.full_days ?? jobMat.schedule?.full_days ?? 1),
        remainder_days: Number(qMat?.schedule?.remainder_days ?? jobMat.schedule?.remainder_days ?? 0),
        total_tolls: Number(qMat?.schedule?.total_tolls ?? jobMat.schedule?.total_tolls ?? 0)
      },
      allocated_trucks: (allocEntry && assignedTrucks.length > 0)
        ? assignedTrucks
        : (Array.isArray(jobMat.allocated_trucks) ? jobMat.allocated_trucks.map(t => {
            if (!t || typeof t !== 'object') return t;
            const dayNum = parseInt(t.scheduled_day ?? (Array.isArray(t.scheduled_day) ? t.scheduled_day[0] : 1), 10) || 1;
            return {
              ...t,
              scheduled_day: dayNum,
              scheduled_date: t.scheduled_date || calculateScheduledDate(baseStartDate, dayNum)
            };
          }) : []),
      load_uhf_channel: loadingUhf || jobMat.load_uhf_channel,
      dispose_uhf_channel: disposalUhf || qMat?.dispose_uhf_channel || jobMat.dispose_uhf_channel || null,
      destination_type: qMat?.destination_type || jobMat.destination_type || (jobMat.yard_id || qMat?.yard_id ? 'commercial' : 'ad_hoc_site'),
      disposal_fee_type: qMat?.disposal_fee_type || jobMat.disposal_fee_type || 'per_tonne',
      disposal_loads_per_trip: Number(qMat?.disposal_loads_per_trip ?? jobMat.disposal_loads_per_trip ?? 1),
      disposal_unit_price: (qMat?.disposal_unit_price !== undefined) ? Number(qMat.disposal_unit_price) : (jobMat.disposal_unit_price !== undefined ? Number(jobMat.disposal_unit_price) : null),
      site_name: qMat?.site_name || jobMat.site_name || qMat?.yard_name || jobMat.yard_name || null,
      geofence_radius: Number(qMat?.geofence_radius ?? jobMat.geofence_radius ?? 200),
      loading_message: siteNotes || jobMat.loading_message
    };

    // Remove obsolete flat legacy operational fields
    delete mappedMat.truck_day_rate;
    delete mappedMat.day_rate;
    delete mappedMat.total_tolls;
    delete mappedMat.load_mins;
    delete mappedMat.tip_mins;
    delete mappedMat.runs_per_day;
    delete mappedMat.total_loads;
    delete mappedMat.estimated_days;
    delete mappedMat.start_time;
    delete mappedMat.finish_time;

    return mappedMat;
  });
}

/**
 * Currency formatter
 */
export function formatCurrency(val) {
  if (val === null || val === undefined || isNaN(val)) return "$0.00";
  return (
    "$" +
    Number(val).toLocaleString("en-AU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

/**
 * Tonnes formatter
 */
export function formatTonnes(val) {
  if (val === null || val === undefined || isNaN(val)) return "0 t";
  return Number(val).toLocaleString("en-AU") + " t";
}

/**
 * Extract display customer name from Quote
 */
export function getQuoteCustomerName(q, companies = []) {
  if (!q) return "Unnamed Customer";
  if (q.customer_summary?.name && typeof q.customer_summary.name === "string" && q.customer_summary.name.trim()) {
    return q.customer_summary.name.trim();
  }
  if (typeof q.customer === "object" && q.customer !== null) {
    if (q.customer.name && typeof q.customer.name === "string" && q.customer.name.trim()) {
      return q.customer.name.trim();
    }
    if (q.customer.company_name && typeof q.customer.company_name === "string" && q.customer.company_name.trim()) {
      return q.customer.company_name.trim();
    }
  }
  if (typeof q.customer === "string" && q.customer.trim() && !/^\d+$/.test(q.customer.trim())) {
    return q.customer.trim();
  }
  if (q.customerName && typeof q.customerName === "string" && q.customerName.trim()) {
    return q.customerName.trim();
  }
  const custId = (typeof q.customer === "number")
    ? q.customer
    : ((typeof q.customer === "object" && q.customer?.id != null)
        ? Number(q.customer.id)
        : (q.customer_id || (typeof q.customer === "string" && /^\d+$/.test(q.customer.trim()) ? Number(q.customer.trim()) : null)));
  if (custId != null && Array.isArray(companies) && companies.length > 0) {
    const matched = companies.find(c => c && Number(c.id || c._id) === custId);
    if (matched?.name && matched.name.trim()) {
      return matched.name.trim();
    }
  }
  if (typeof q.customer === "string" && q.customer.trim()) {
    return q.customer.trim();
  }
  return "Unnamed Customer";
}

/**
 * Extract site address from Quote
 */
export function getQuoteSiteAddress(q) {
  if (!q) return "Site address not specified";
  const sumAddr = typeof q.customer_summary?.address === "string" ? q.customer_summary.address : q.customer_summary?.address?.street;
  return (
    q.quote_materials?.[0]?.load_address?.street ||
    sumAddr ||
    q.customer?.site_address ||
    q.siteAddress ||
    (typeof q.customer === "object" ? q.customer?.address?.street : "") ||
    "Site address not specified"
  );
}

/**
 * Calculate total tonnes from Quote
 */
export function getQuoteTotalTonnes(q) {
  if (!q) return 0;
  if (q.financial_summary?.total_tonnes != null) {
    return q.financial_summary.total_tonnes;
  }
  if (q.quote_materials && q.quote_materials.length > 0) {
    return q.quote_materials.reduce(
      (acc, m) => acc + Number(m.total_tonnes || m.quantity || 0),
      0
    );
  }
  if (q.haulagePackages && q.haulagePackages.length > 0) {
    return q.haulagePackages.reduce(
      (acc, p) => acc + Number(p.totalTonnes || p.tonnesDeliveredInDay || 0),
      0
    );
  }
  return Number(q.tonnes || 0);
}

/**
 * Calculate grand total from Quote
 */
export function getQuoteGrandTotal(q) {
  if (!q) return 0;
  if (q.financial_summary?.grand_total_inc_gst != null) {
    return q.financial_summary.grand_total_inc_gst;
  }
  if (q.quote_materials && q.quote_materials.length > 0) {
    return q.quote_materials.reduce(
      (acc, m) =>
        acc + Number(m.proposed_total_price || m.financial_breakdown?.total_inc_gst || 0),
      0
    );
  }
  return 0;
}

/**
 * Extract display customer name from Job
 * Resolves strictly per-job with no reliance or leaking from any active/selected quote.
 */
export function getJobCustomerName(job, quotes = [], companies = []) {
  if (!job) return "Unnamed Customer";

  // If a caller passed (job, selectedQuote, quotes, companies) for backward compat:
  let qList = quotes;
  let compList = companies;
  if (Array.isArray(arguments[2]) || Array.isArray(arguments[3])) {
    if (!Array.isArray(arguments[1]) && Array.isArray(arguments[2])) {
      qList = arguments[2];
      compList = arguments[3] || [];
    }
  }

  // 1. Direct object customer on Job with non-empty name or company_name
  if (typeof job.customer === "object" && job.customer !== null) {
    if (job.customer.name && typeof job.customer.name === "string" && job.customer.name.trim()) {
      return job.customer.name.trim();
    }
    if (job.customer.company_name && typeof job.customer.company_name === "string" && job.customer.company_name.trim()) {
      return job.customer.company_name.trim();
    }
  }

  // 2. Direct string customer on Job (non-numeric)
  if (typeof job.customer === "string" && job.customer.trim() && !/^\d+$/.test(job.customer.trim())) {
    return job.customer.trim();
  }

  // 3. Direct customerName or customer_summary or company on Job
  if (job.customerName && typeof job.customerName === "string" && job.customerName.trim()) {
    return job.customerName.trim();
  }
  if (job.customer_summary?.name && typeof job.customer_summary.name === "string" && job.customer_summary.name.trim()) {
    return job.customer_summary.name.trim();
  }
  if (job.company?.name && typeof job.company.name === "string" && job.company.name.trim()) {
    return job.company.name.trim();
  }

  // 4. Look up specific linked quote from quotes list for THIS job
  const quoteArray = Array.isArray(qList) ? qList : [];
  const linkedQ = quoteArray.find(q =>
    (job.quote_id != null && (String(q.id || q._id) === String(job.quote_id) || String(q.quote_id) === String(job.quote_id))) ||
    (job.quote_number && q.quote_number && String(q.quote_number).toLowerCase() === String(job.quote_number).toLowerCase()) ||
    (job.id != null && q.job_id != null && String(q.job_id) === String(job.id))
  );
  if (linkedQ) {
    const qName = getQuoteCustomerName(linkedQ, compList);
    if (qName && qName !== "Unnamed Customer") return qName;
  }

  // 5. Look up in companies table if customer or customer_id is a number/ID
  const custId = (typeof job.customer === "number")
    ? job.customer
    : ((typeof job.customer === "object" && job.customer?.id != null)
        ? Number(job.customer.id)
        : (job.customer_id || (typeof job.customer === "string" && /^\d+$/.test(job.customer.trim()) ? Number(job.customer.trim()) : null)));
  if (custId != null && Array.isArray(compList) && compList.length > 0) {
    const matchedComp = compList.find(c => c && (Number(c.id || c._id) === custId));
    if (matchedComp?.name && matchedComp.name.trim()) {
      return matchedComp.name.trim();
    }
  }

  // 6. Fallback to string customer if present
  if (typeof job.customer === "string" && job.customer.trim()) {
    return job.customer.trim();
  }

  return "Unnamed Customer";
}

/**
 * Extract site address from Job
 */
export function getJobSiteAddress(job, quotes = []) {
  if (!job) return "Site address not specified";
  const firstMat = Array.isArray(job.materials) ? job.materials[0] : (Array.isArray(job.job_materials) ? job.job_materials[0] : null);
  const matAddr = firstMat?.load_address || firstMat?.siteAddress || firstMat?.loadAddress;
  if (matAddr) {
    if (typeof matAddr === "string" && matAddr.trim()) return matAddr.trim();
    if (matAddr.place_name && matAddr.place_name.trim()) return matAddr.place_name.trim();
    const parts = [matAddr.street, matAddr.suburb, matAddr.state].filter(Boolean);
    if (parts.length > 0) return parts.join(", ");
  }
  if (job.siteAddress && typeof job.siteAddress === "string" && job.siteAddress.trim()) {
    return job.siteAddress.trim();
  }
  if (job.load_address) {
    if (typeof job.load_address === "string" && job.load_address.trim()) return job.load_address.trim();
    if (job.load_address.place_name && job.load_address.place_name.trim()) return job.load_address.place_name.trim();
    const parts = [job.load_address.street, job.load_address.suburb, job.load_address.state].filter(Boolean);
    if (parts.length > 0) return parts.join(", ");
  }
  const addr = job.address || (typeof job.customer === "object" ? job.customer?.address : null);
  if (addr) {
    if (typeof addr === "string" && addr.trim()) return addr.trim();
    const parts = [addr.street, addr.suburb, addr.state].filter(Boolean);
    if (parts.length > 0) return parts.join(", ");
  }

  // Fallback to linked quote's address if this job has a linked quote
  if (Array.isArray(quotes) && quotes.length > 0) {
    const linkedQ = quotes.find(q =>
      (job.quote_id != null && (String(q.id || q._id) === String(job.quote_id) || String(q.quote_id) === String(job.quote_id))) ||
      (job.quote_number && q.quote_number && String(q.quote_number).toLowerCase() === String(job.quote_number).toLowerCase())
    );
    if (linkedQ) {
      const qAddr = getQuoteSiteAddress(linkedQ);
      if (qAddr && qAddr !== "Site address not specified") return qAddr;
    }
  }

  return "Site address not specified";
}

/**
 * Calculate total tonnes from Job
 */
export function getJobTotalTonnes(job) {
  if (!job) return 0;
  if (job.total_tonnes != null && !isNaN(job.total_tonnes)) return Number(job.total_tonnes);
  if (job.financial_summary?.total_tonnes != null) return Number(job.financial_summary.total_tonnes);
  const mats = Array.isArray(job.job_materials) ? job.job_materials : (Array.isArray(job.materials) ? job.materials : []);
  return mats.reduce((acc, m) => acc + Number(m.amount || m.quantity || m.total_tonnes || 0), 0);
}

/**
 * Calculate total allocated trucks from Job
 */
export function getJobTotalAllocatedTrucks(job) {
  if (!job) return 0;
  if (Array.isArray(job.allocated_trucks)) return job.allocated_trucks.length;
  if (Array.isArray(job.assigned_trucks)) return job.assigned_trucks.length;
  const mats = Array.isArray(job.job_materials) ? job.job_materials : (Array.isArray(job.materials) ? job.materials : []);
  return mats.reduce((acc, m) => acc + (Array.isArray(m.allocated_trucks) ? m.allocated_trucks.length : 0), 0);
}
